import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAdminRealtime } from "@/lib/admin-api";
import {
  notificationsApi,
  type AdminNotification,
  type NotificationListResponse,
  type NotificationFilters,
} from "@/lib/admin-api/notifications";

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

const NOTIFICATIONS_KEY = ["admin", "notifications"] as const;
const UNREAD_COUNT_KEY = ["admin", "notifications", "unread-count"] as const;

function listKey(filters: NotificationFilters) {
  return [...NOTIFICATIONS_KEY, "list", filters] as const;
}

// ---------------------------------------------------------------------------
// List hook
// ---------------------------------------------------------------------------

export function useAdminNotifications(filters: NotificationFilters = {}) {
  // Subscribe to realtime changes on notification_queue
  useAdminRealtime("notification_queue", ["admin", "notifications"]);

  return useQuery<NotificationListResponse>({
    queryKey: listKey(filters),
    queryFn: () => notificationsApi.list(filters),
    staleTime: 15_000,
    refetchOnWindowFocus: true,
  });
}

// ---------------------------------------------------------------------------
// Unread count hook
// ---------------------------------------------------------------------------

export function useUnreadCount() {
  // Supabase Realtime pushes notification_queue changes — no need to poll
  // aggressively. The interval below is a backup for missed events only.
  useAdminRealtime("notification_queue", ["admin", "notifications"]);

  return useQuery<number>({
    queryKey: [...UNREAD_COUNT_KEY],
    queryFn: async () => {
      const res = await notificationsApi.unreadCount();
      return res.count;
    },
    // Audit S-M5: 30s polling was making 10+ calls per session even though
    // realtime pushes handle invalidation. Relaxed to 2 min; realtime is
    // still the primary delivery mechanism.
    staleTime: 60_000,
    refetchOnWindowFocus: true,
    refetchInterval: 120_000,
  });
}

// ---------------------------------------------------------------------------
// Mark as read mutation
// ---------------------------------------------------------------------------

export function useMarkAsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => notificationsApi.markRead(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...NOTIFICATIONS_KEY] });
      queryClient.invalidateQueries({ queryKey: [...UNREAD_COUNT_KEY] });
    },
  });
}

// ---------------------------------------------------------------------------
// Mark all as read mutation
// ---------------------------------------------------------------------------

export function useMarkAllAsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => notificationsApi.markAllRead(),
    onMutate: async () => {
      // Optimistic update: set unread count to 0
      await queryClient.cancelQueries({ queryKey: [...UNREAD_COUNT_KEY] });
      const previous = queryClient.getQueryData<number>([...UNREAD_COUNT_KEY]);
      queryClient.setQueryData([...UNREAD_COUNT_KEY], 0);
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData([...UNREAD_COUNT_KEY], context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [...NOTIFICATIONS_KEY] });
      queryClient.invalidateQueries({ queryKey: [...UNREAD_COUNT_KEY] });
    },
  });
}

// ---------------------------------------------------------------------------
// Delete many mutation
// ---------------------------------------------------------------------------

export function useDeleteNotifications() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (ids: readonly string[]) => notificationsApi.deleteMany(ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...NOTIFICATIONS_KEY] });
      queryClient.invalidateQueries({ queryKey: [...UNREAD_COUNT_KEY] });
    },
  });
}

// Re-export types for convenience
export type { AdminNotification, NotificationFilters };
